/**
 * Armada PIM Accordions — reactive boot SPECIFICATIONS (AAS-1138).
 *
 * Weight (`pim.weight_g__boot_size`) and Alpine Boot Last (`pim.last_width`) are
 * variant-level and vary by shell size. This section is separate from the main
 * product section, so a size change doesn't re-render it via the Section
 * Rendering API. This script keeps the two rows in sync by reading a per-size
 * map embedded in the section and listening for the theme's `variantChange`
 * event — the same pattern as armada-about.js (flex profile).
 *
 * Map is keyed by the "Size" option value (deduped) so a colour change never
 * alters the values. See docs/pim-field-migration.md.
 */
(function () {
  function initSpecs(root) {
    var dataEl = root.querySelector('script[type="application/json"][data-boot-specs-map]');
    if (!dataEl) return;

    var map;
    try {
      map = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    // 1-based index of the "Size" option in variant.options; 0 = no Size option
    // (map is then keyed by variant id instead).
    var sizeIndex = parseInt(dataEl.getAttribute('data-size-index') || '0', 10);

    var weightRow = root.querySelector('[data-boot-weight-row]');
    var weightVal = root.querySelector('[data-boot-weight]');
    var lastRow = root.querySelector('[data-boot-last-row]');
    var lastVal = root.querySelector('[data-boot-last]');
    // The whole SPECIFICATIONS dropdown — hidden when the selected size has no specs.
    var details = root.closest('details');

    function hasValue(v) {
      return v !== null && v !== undefined && v !== '';
    }

    function setRow(row, valEl, value, suffix) {
      if (!row || !valEl) return;
      if (!hasValue(value)) {
        row.hidden = true;
        valEl.textContent = '';
      } else {
        valEl.textContent = suffix ? value + suffix : String(value);
        row.hidden = false;
      }
    }

    function apply(entry) {
      entry = entry || {};
      setRow(weightRow, weightVal, entry.weight, ' g');
      setRow(lastRow, lastVal, entry.last, '');
      // Boots only carry these two spec rows, so hide the dropdown when neither
      // is present for the selected size (and reveal it again when one is).
      if (details) details.hidden = !(hasValue(entry.weight) || hasValue(entry.last));
    }

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      // Event fires from the main product section (no sectionId filter). Look up
      // by SIZE so a colour-only change never alters the specs.
      var variant = event && event.data && event.data.variant;
      if (!variant) return;
      var key;
      if (sizeIndex > 0 && Array.isArray(variant.options)) {
        key = variant.options[sizeIndex - 1];
      } else {
        key = variant.id;
      }
      if (key == null || !(key in map)) return; // not a known size / variant
      apply(map[key]);
    });
  }

  var DURATION = 300;
  var EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // ALL FEATURES — button disclosure with a peek (a faded preview of the first
  // rows) when collapsed, expanding to the full list with a height animation.
  // Native <details> can't show content while closed, hence the button.
  function initPeekAccordion(container) {
    var btn = container.querySelector('.armada-apparel-about__accordion-trigger');
    var content = container.querySelector('.armada-apparel-about__accordion-content--peek');
    if (!btn || !content) return;

    // Progressive enhancement: the peek clamp only applies once JS runs, so the
    // full list stays visible without JS.
    container.classList.add('is-enhanced');
    var isOpen = false;
    var animating = false;

    // Resolve --features-peek-height (rem/px) to px via a throwaway probe so the
    // JS never has to assume the root font-size.
    function peekPx() {
      var v = getComputedStyle(container).getPropertyValue('--features-peek-height').trim() || '7rem';
      var probe = document.createElement('div');
      probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;height:' + v;
      container.appendChild(probe);
      var px = probe.offsetHeight;
      container.removeChild(probe);
      return px;
    }

    function setOpen(next) {
      if (animating || next === isOpen) return;
      isOpen = next;
      btn.setAttribute('aria-expanded', String(isOpen));
      container.classList.toggle('is-open', isOpen);

      if (prefersReducedMotion()) {
        content.style.transition = 'none';
        content.style.height = isOpen ? 'auto' : peekPx() + 'px';
        return;
      }

      animating = true;
      var from = content.offsetHeight;
      var to = isOpen ? content.scrollHeight : peekPx();
      content.style.height = from + 'px';
      content.style.transition = 'height ' + DURATION + 'ms ' + EASE;
      requestAnimationFrame(function () {
        content.style.height = to + 'px';
      });
      content.addEventListener('transitionend', function te(ev) {
        if (ev.propertyName !== 'height') return;
        content.removeEventListener('transitionend', te);
        content.style.transition = '';
        // Open: release to auto so it reflows (responsive 1↔2 columns);
        // collapsed: hold the peek height.
        content.style.height = isOpen ? 'auto' : peekPx() + 'px';
        animating = false;
      });
    }

    // Initial collapsed state.
    content.style.height = peekPx() + 'px';
    btn.setAttribute('aria-expanded', 'false');
    btn.addEventListener('click', function () { setOpen(!isOpen); });
  }

  // Native <details> accordions (Composition / Specifications / Care) — animate
  // the content height on open/close so every accordion shares the same motion.
  // Reduced motion falls back to the native instant toggle.
  function initAnimAccordion(details) {
    var summary = details.querySelector('summary');
    var content = details.querySelector('.armada-apparel-about__accordion-content');
    if (!summary || !content) return;
    var animating = false;

    function finish() {
      content.style.transition = '';
      content.style.height = '';
      content.style.overflow = '';
      animating = false;
    }

    summary.addEventListener('click', function (e) {
      if (prefersReducedMotion()) return; // let the native toggle handle it
      e.preventDefault();
      if (animating) return;
      animating = true;
      content.style.overflow = 'hidden';

      if (details.open) {
        // CLOSE: animate current height → 0, then drop the open attribute.
        var startH = content.offsetHeight;
        content.style.height = startH + 'px';
        content.style.transition = 'height ' + DURATION + 'ms ' + EASE;
        requestAnimationFrame(function () { content.style.height = '0px'; });
        content.addEventListener('transitionend', function te(ev) {
          if (ev.propertyName !== 'height') return;
          content.removeEventListener('transitionend', te);
          details.open = false;
          finish();
        });
      } else {
        // OPEN: set open so the content renders, then animate 0 → its height.
        details.open = true;
        var targetH = content.scrollHeight;
        content.style.height = '0px';
        content.style.transition = 'height ' + DURATION + 'ms ' + EASE;
        requestAnimationFrame(function () { content.style.height = targetH + 'px'; });
        content.addEventListener('transitionend', function te(ev) {
          if (ev.propertyName !== 'height') return;
          content.removeEventListener('transitionend', te);
          finish();
        });
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-boot-specs]').forEach(initSpecs);
    document.querySelectorAll('[data-peek-accordion]').forEach(initPeekAccordion);
    document
      .querySelectorAll('.armada-pim-accordions details.armada-apparel-about__accordion')
      .forEach(initAnimAccordion);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
